import React, { useState, useEffect, memo } from 'react';
import { View, StyleSheet, ActivityIndicator, Platform, Text } from 'react-native';
import { Video, ResizeMode } from 'expo-av';
import YoutubePlayer from "react-native-youtube-iframe";

type VideoPlayerProps = {
  videoUrl: string;
  thumbnailUrl?: string;
  type?: 'native' | 'youtube';
};

const getYoutubeVideoId = (url: string) => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

const VideoPlayer: React.FC<VideoPlayerProps> = memo(({ videoUrl, thumbnailUrl, type = 'native' }) => {
  const video = React.useRef<Video>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Web用のYouTube判定とレンダリング
  if (Platform.OS === 'web' && type === 'youtube') {
    const videoId = getYoutubeVideoId(videoUrl);
    if (!videoId) {
      return (
        <View style={styles.container}>
          <Text style={styles.errorText}>Invalid YouTube URL</Text>
        </View>
      );
    }

    return (
      <View style={styles.container}>
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0, border: 'none' }}
          onLoad={() => setIsLoading(false)}
        />
        {isLoading && (
          <View style={styles.loadingContainer} pointerEvents="none">
            <ActivityIndicator size="large" color="#fff" />
          </View>
        )}
      </View>
    );
  }

  // Web用のネイティブ動画判定（HLS対応の警告付き）
  if (Platform.OS === 'web' && type === 'native') {
    return (
      <View style={styles.container}>
        <video
          src={videoUrl}
          poster={thumbnailUrl}
          controls
          playsInline
          style={{ width: '100%', height: '100%', backgroundColor: '#000', objectFit: 'contain' }}
          onCanPlay={() => setIsLoading(false)}
          onError={(e) => {
            console.error('Web Video error:', e);
            if (videoUrl.includes('.m3u8')) {
              setError('HLS非対応ブラウザです。Safariを使用するか、リンクをコピーして他のプレーヤーで開いてください。');
            } else {
              setError('動画の読み込みに失敗しました');
            }
            setIsLoading(false);
          }}
        />
        {isLoading && (
          <View style={styles.loadingContainer} pointerEvents="none">
            <ActivityIndicator size="large" color="#fff" />
          </View>
        )}
        {error && (
          <View style={[styles.loadingContainer, { backgroundColor: 'rgba(0,0,0,0.9)' }]}>
            <Text style={styles.errorText}>{error}</Text>
            {videoUrl.includes('.m3u8') && (
              <Text 
                style={{ color: '#3ea6ff', marginTop: 10, cursor: 'pointer' }}
                onPress={() => window.open(videoUrl, '_blank')}
              >
                直接開く（対応ブラウザのみ）
              </Text>
            )}
          </View>
        )}
      </View>
    );
  }

  // URLが変わったときだけ内部状態をリセット
  useEffect(() => {
    setIsLoading(true);
    setError(null);
  }, [videoUrl]);

  const onLayout = (event: any) => {
    const { width } = event.nativeEvent.layout;
    if (width > 0 && width !== containerWidth) {
      setContainerWidth(width);
    }
  };

  if (type === 'youtube') {
    const videoId = getYoutubeVideoId(videoUrl);
    if (!videoId) {
      return (
        <View style={styles.container}>
          <Text style={styles.errorText}>Invalid YouTube URL</Text>
        </View>
      );
    }

    return (
      <View style={styles.container} onLayout={onLayout}>
        {containerWidth > 0 && (
          <YoutubePlayer
            height={containerWidth * (9 / 16)}
            width={containerWidth}
            play={false}
            videoId={videoId}
            onReady={() => setIsLoading(false)}
            onError={(e) => {
              console.error('YouTube error:', e);
              setError('YouTube playback error');
              setIsLoading(false);
            }}
          />
        )}
        {isLoading && (
          <View style={styles.loadingContainer} pointerEvents="none">
            <ActivityIndicator size="large" color="#fff" />
          </View>
        )}
      </View>
    );
  }

  return (
    <View style={styles.container} onLayout={onLayout}>
      <Video
        ref={video}
        style={styles.video}
        source={{ uri: videoUrl }}
        posterSource={thumbnailUrl ? { uri: thumbnailUrl } : undefined}
        usePoster={!!thumbnailUrl}
        useNativeControls
        resizeMode={ResizeMode.CONTAIN}
        onLoad={() => {
          setIsLoading(false);
          setError(null);
        }}
        onError={(err) => {
          console.error('Video playback error:', err);
          // HLS (m3u8) かつ Web (iOSを除く) の場合はエラーメッセージを詳細にする
          if (Platform.OS === 'web' && videoUrl.includes('.m3u8')) {
            setError('お使いのブラウザはこの高画質動画形式(HLS)の再生をサポートしていません。Safariを使用するか、YouTube動画をお楽しみください。');
          } else {
            setError('再生エラー: 動画を読み込めませんでした');
          }
          setIsLoading(false);
        }}
        shouldPlay={false}
      />
      {isLoading && (
        <View style={styles.loadingContainer} pointerEvents="none">
          <ActivityIndicator size="large" color="#fff" />
        </View>
      )}
      {error && (
        <View style={[styles.loadingContainer, { backgroundColor: 'rgba(0,0,0,0.9)' }]}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
    </View>
  );
});

const styles = StyleSheet.create({
  container: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#000',
    position: 'relative',
    overflow: 'hidden',
  },
  video: {
    width: '100%',
    height: '100%',
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
  },
  errorText: {
    color: '#ff4444',
    fontSize: 14,
    textAlign: 'center',
    padding: 20,
  },
  webHlsNotice: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    padding: 4,
  },
  webHlsNoticeText: {
    color: '#fff',
    fontSize: 10,
    textAlign: 'center',
  },
});

export default VideoPlayer;
